'use client';

import React, { useMemo } from 'react';
import { useHits } from 'react-instantsearch';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import avo from 'analytics/avo';
import useUserCurrentPageType from 'hooks/useUserCurrentPageType';
import useSearchUrlQueryParams from '../../../advanced-search/hooks/useSearchUrlQueryParams';

const ResultsHeader = ({ variant = 'desktop', children }) => (
  <div
    className={classNames('mx-0 my-2.5 text-base font-bold', {
      'text-gray': variant !== 'mobile',
      'text-brand block mt-[15px] mb-[7px]': variant === 'mobile',
    })}
  >
    {children}
  </div>
);

ResultsHeader.propTypes = {
  variant: PropTypes.string,
  children: PropTypes.node,
};

/**
 * CategoryHits component for Next version (v7)
 * Uses useHits hook instead of connectHits
 */
const CategoryHitsNext = ({ toggleSearchOpen, variant = 'desktop' }) => {
  const { hits } = useHits();
  const { queryUrlParam: query } = useSearchUrlQueryParams();
  const currentPage = useUserCurrentPageType();

  const items = useMemo(
    () =>
      hits.reduce((acc, hit) => {
        const highlightedCategory =
          hit?._highlightResult?.category_name?.value || '';
        const categoryLabelRaw =
          hit?.category_name || highlightedCategory.replace(/<[^>]+>/g, '');
        const categoryLabel = categoryLabelRaw?.trim();

        if (!categoryLabel) {
          return acc;
        }

        // Ensure we have a valid URL for the Link component
        const categoryUrl =
          hit.category_sef_url ||
          hit.url ||
          `/categories/${categoryLabel.toLowerCase().replace(/\s+/g, '-')}`;

        acc.push({ objectID: hit.objectID, categoryLabel, categoryUrl });
        return acc;
      }, []),
    [hits]
  );

  if (!items.length) {
    return null;
  }

  return (
    <div
      className={classNames(
        '[border-right:1px_solid_#f1f1f1] pt-5 pr-8 pb-10 min-w-[20%] flex-[0_1_20%] pl-[30px] h-full min-h-[264px]',
        {
          '!p-0 min-h-[auto] mb-2 w-full border-0 order-[-1]':
            variant === 'mobile',
        }
      )}
    >
      <ResultsHeader variant={variant}>Categories</ResultsHeader>
      {items.map(item => (
        <div key={item.objectID}>
          <Link
            className={classNames(
              'text-sm font-bold mt-1 mb-1 ml-0 no-underline !leading-[1.5rem] active:bg-gray-light8 focus:bg-gray-light8 hover:bg-gray-light8',
              {
                'block mr-2.5 text-[#585858]': variant === 'desktop',
                'text-sm px-2 py-1 rounded-[3px] text-gray font-bold mr-2.5 bg-gray-light9 inline-block':
                  variant === 'mobile',
              }
            )}
            onClick={async () => {
              await toggleSearchOpen();

              avo.productsSearched({
                query,
                location: currentPage,
                searchTarget: avo.SearchTarget.OTHER_RESULTS_LINK,
              });
            }}
            to={item.categoryUrl}
          >
            {item.categoryLabel}
          </Link>
        </div>
      ))}
    </div>
  );
};

CategoryHitsNext.propTypes = {
  toggleSearchOpen: PropTypes.func,
  variant: PropTypes.string,
};

export default CategoryHitsNext;
